import { Card, Skeleton } from '@morpho-org/ui-v6/server'
import { cn } from '@morpho-org/ui-v6/utils'
import { ChartSkeleton } from '@repo/ui'

import { CHART_SKELETON_GEOMETRY } from './ChartStatusLoading'

export interface ChartCardLoadingProps {
  /** Card layout classes, copied from the loaded card (gap, padding, grow). */
  className?: string
  padding?: 's' | 'm' | 'none'
  /** Full chart height, axis labels included — the `height` passed to the loaded chart. */
  chartClassName: string
  /** Header placeholder. `false` for cards whose chart is the whole card. */
  header?: boolean
  /** Width of the title stub. */
  titleClassName?: string
  /** Size of the big value stub under the title; omit when the card has no value. */
  valueClassName?: string
  /** Gap between the title column rows. */
  titleColumnClassName?: string
  /** Header-right controls (selects, segment controls): one size class per control. */
  controls?: string[]
  /** Controls the loaded card moves under the title below `md`; hides `controls` there. */
  mobileControls?: string[]
  /** Header wraps its controls onto a new row when they don't fit (most chart cards). */
  wrapHeader?: boolean
  /** Footer caption under the chart (e.g. "Oracle price change"). */
  footer?: boolean
}

/**
 * Suspense fallback for a chart card. Mirrors the loaded card's layout — header
 * rows, controls and the chart at its real height — so nothing moves when data arrives.
 */
function ChartCardLoading({
  className,
  padding,
  chartClassName,
  header = true,
  titleClassName = 'w-[140px]',
  valueClassName,
  titleColumnClassName = 'gap-xs',
  controls = [],
  mobileControls,
  wrapHeader = true,
  footer = false
}: ChartCardLoadingProps) {
  const chart = <ChartSkeleton className={chartClassName} {...CHART_SKELETON_GEOMETRY} />
  return (
    <Card
      className={cn('w-full flex-col', padding === undefined && 'py-lg px-md md:px-lg', className)}
      padding={padding}
      withBorder
    >
      {header && (
        <div className={cn('gap-sm flex items-center justify-between', wrapHeader && 'flex-wrap')}>
          <div className={cn('flex flex-col', titleColumnClassName)}>
            <div className="h-md flex items-center">
              <Skeleton className={cn('h-4', titleClassName)} />
            </div>
            {mobileControls && (
              <div className="gap-sm flex flex-wrap items-center md:hidden">
                {mobileControls.map((size, index) => (
                  <Skeleton key={index} className={cn('rounded-lg', size)} />
                ))}
              </div>
            )}
            {valueClassName && <Skeleton className={cn('rounded-[20px]', valueClassName)} />}
          </div>
          {controls.length > 0 && (
            <div
              className={cn(
                'gap-sm flex min-w-0 flex-wrap items-center justify-end',
                mobileControls && 'max-md:hidden'
              )}
            >
              {controls.map((size, index) => (
                <Skeleton key={index} className={cn('rounded-lg', size)} />
              ))}
            </div>
          )}
        </div>
      )}
      {footer ? (
        <div className="gap-2xs flex flex-col">
          {chart}
          <div className="mt-2xs h-md flex items-center justify-end">
            <Skeleton className="h-4 w-[110px]" />
          </div>
        </div>
      ) : (
        chart
      )}
    </Card>
  )
}

export default ChartCardLoading
